import { useEffect, useMemo, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
} from "recharts";
import {
  TrendUp, TrendDown, Receipt, Wallet, ArrowsLeftRight, Sparkle, ArrowRight, Plus, CaretRight, FilePdf,
  Clock, WarningCircle, CheckCircle, ChartBar, ArrowsClockwise, Briefcase, User,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money, monthLabel, shortDate } from "@/lib/format";
import { Card, Loading, EmptyState, SectionTitle, MerchantLogo } from "@/components/riq";
import ReferralCard from "@/components/ReferralCard";
import { HomeBasketCard } from "@/components/CheapestBasket";
import { sourceMeta } from "@/components/SourceBadge";
import { periodLabel } from "@/pages/Warranties";
import { SubscriptionDialog } from "@/pages/Subscriptions";

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openAddReceipt } = useOutletContext();
  const [data, setData] = useState(null);
  const [subs, setSubs] = useState(null);
  const [addSub, setAddSub] = useState(null); // {} to open add dialog
  const cur = user?.currency || "ZAR";

  const loadSubs = () => api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => setSubs(false));
  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => setData(false));
    loadSubs();
  }, []);

  if (data === null) return <Loading label="Loading your dashboard…" />;
  if (data === false) return <div className="text-muted-foreground">Could not load dashboard.</div>;

  const { summary, categories, budget, timeseries, monthly_trend, recent_receipts, insights, warranties, flagged } = data;
  const empty = summary.receipt_count === 0 && recent_receipts.length === 0;

  // Merge subscription monthly-equivalent costs into the main expense donut & total.
  const subsCats = subs && subs.categories ? subs.categories : [];
  const subsMonthly = (subs && subs.summary && subs.summary.monthly_total) || 0;
  const subsCount = (subs && subs.summary && subs.summary.count) || 0;
  const mergedMap = {};
  (categories || []).forEach((c) => { mergedMap[c.category] = (mergedMap[c.category] || 0) + c.total; });
  subsCats.forEach((c) => { mergedMap[c.category] = (mergedMap[c.category] || 0) + c.monthly_cost; });
  const mergedCategories = Object.entries(mergedMap)
    .map(([category, total]) => ({ category, total: Math.round(total * 100) / 100 }))
    .sort((a, b) => b.total - a.total);
  const donutTotal = Math.round(((summary.total_spend || 0) + subsMonthly) * 100) / 100;

  // Subscription-inclusive headline totals (kept consistent with the donut).
  const totalWithSubs = donutTotal;
  const countWithSubs = (summary.receipt_count || 0) + subsCount;
  const avgWithSubs = countWithSubs ? Math.round((totalWithSubs / countWithSubs) * 100) / 100 : 0;
  const prevWithSubs = (summary.previous_total || 0) + subsMonthly;
  const changeWithSubs = prevWithSubs ? Math.round(((totalWithSubs - prevWithSubs) / prevWithSubs) * 1000) / 10 : null;
  const budgetSpent = Math.round(((budget.spent || 0) + subsMonthly) * 100) / 100;
  const budgetRemaining = budget.monthly_budget != null ? Math.round((budget.monthly_budget - budgetSpent) * 100) / 100 : null;
  const budgetPctUsed = budget.monthly_budget ? Math.round((budgetSpent / budget.monthly_budget) * 1000) / 10 : null;

  if (empty) {
    return (
      <EmptyState
        icon={Receipt}
        title="Scan your first receipt"
        subtitle="Scan your first receipt to start understanding your spending. We'll turn it into structured, searchable data automatically."
        action={<button data-testid="empty-add-receipt" onClick={openAddReceipt} className="inline-flex items-center gap-2 rounded-full bg-riq-primary text-white px-6 py-3 font-medium hover:-translate-y-0.5 transition-transform"><Plus size={18} weight="bold" /> Add Receipt</button>}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div className="flex items-end justify-between">
        <div className="min-w-0">
          <h1 className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-riq-primary">Hi, {user?.name?.split(" ")[0] || "there"} 👋</h1>
          {user?.account_type === "business" ? (
            <span data-testid="home-account-type-badge" className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-riq-primary/10 text-riq-primary px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              <Briefcase size={13} weight="fill" /> Business account
            </span>
          ) : (
            <span data-testid="home-account-type-badge" className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-teal-100 text-teal-700 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
              <User size={13} weight="fill" /> Personal account
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="home-reports-link" onClick={() => navigate("/reports")} title="Export a PDF report"
            className="grid h-10 w-10 sm:h-11 sm:w-11 place-items-center rounded-full border border-riq-border bg-white text-riq-primary hover:-translate-y-0.5 hover:border-riq-primary transition-all shrink-0">
            <FilePdf size={20} weight="duotone" />
          </button>
          <button data-testid="home-add-receipt" onClick={openAddReceipt} className="hidden sm:flex items-center gap-2 rounded-full bg-riq-primary text-white px-5 py-2.5 font-medium hover:-translate-y-0.5 transition-transform"><Plus size={18} weight="bold" /> Add Receipt</button>
        </div>
      </div>

      {/* Spending hero */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
        <Card className="bg-riq-primary text-white relative overflow-hidden !p-0">
          <div className="absolute inset-0 opacity-[0.07] riq-noise" />
          <div className="relative p-6 sm:p-7">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/60">Your spending · {monthLabel()}</div>
            <div className="mt-2 flex flex-wrap items-end gap-x-4 gap-y-2">
              <div data-testid="home-total-spend" className="font-head text-4xl sm:text-5xl font-bold tracking-tight drop-shadow-sm break-words">{money(totalWithSubs, cur)}</div>
              {changeWithSubs != null && (
                <div className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium ${changeWithSubs >= 0 ? "bg-white/10 text-riq-gold" : "bg-white/10 text-riq-sage"}`}>
                  {changeWithSubs >= 0 ? <TrendUp size={16} weight="bold" /> : <TrendDown size={16} weight="bold" />}
                  {Math.abs(changeWithSubs)}% vs last month
                </div>
              )}
            </div>
            {subsMonthly > 0 && <div className="mt-1 text-xs text-white/60">incl. {money(subsMonthly, cur)} in subscriptions</div>}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:max-w-md">
              <HeroStat testId="home-receipt-count" icon={Receipt} label="Receipts" value={summary.receipt_count} />
              <HeroStat testId="home-avg-transaction" icon={ArrowsLeftRight} label="Avg. transaction" value={money(avgWithSubs, cur)} />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Monthly Expense Trend */}
      <MonthlyTrend weekly={timeseries} monthly={monthly_trend} subsList={(subs && subs.subscriptions) ? subs.subscriptions.filter((s) => s.active) : []} cur={cur} />

      {/* Cheapest basket for the last grocery shop */}
      <HomeBasketCard />

      {/* Invite friends */}
      <ReferralCard variant="compact" />

      {/* Expense Categories donut (incl. subscriptions) */}
      <CategoryDonut
        categories={mergedCategories}
        total={donutTotal}
        subsTotal={subsMonthly}
        cur={cur}
        onSelect={(c) => navigate(`/analytics?category=${encodeURIComponent(c)}`)}
        onAddSubscription={() => setAddSub({})}
      />

      {/* Recent Transactions */}
      <div>
        <SectionTitle action={<button data-testid="home-all-receipts" onClick={() => navigate("/receipts")} className="text-sm text-riq-primary flex items-center gap-1">All receipts <ArrowRight size={14} /></button>}>Recent Transactions</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {recent_receipts.map((r) => (
            <button key={r.id} data-testid={`recent-receipt-${r.id}`} onClick={() => navigate(`/receipts/${r.id}`)} className="text-left">
              <Card className="flex items-center gap-3 p-4 hover:-translate-y-0.5 transition-transform">
                <MerchantLogo name={r.merchant_name} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate flex items-center gap-1.5">
                    <span className="truncate">{r.merchant_name}</span>
                    {r.review_code === "duplicate" && <span data-testid={`recent-duplicate-badge-${r.id}`} className="rounded-full bg-[#FFF0D6] text-[#8a5a00] px-2 py-0.5 text-[10px] font-bold uppercase shrink-0">Duplicate</span>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{shortDate(r.date)} · {r.merchant_category}</div>
                </div>
                <div className="font-head font-bold text-riq-primary shrink-0">{money(r.total, cur)}</div>
              </Card>
            </button>
          ))}
        </div>
      </div>

      {/* Needs attention (rejected / flagged) */}
      {flagged?.items?.length > 0 && (
        <div>
          <SectionTitle action={<button data-testid="home-see-flagged" onClick={() => navigate("/receipts")} className="text-sm text-riq-primary flex items-center gap-1">All receipts <ArrowRight size={14} /></button>}>Needs attention</SectionTitle>
          <Card data-testid="home-flagged" className="p-0 overflow-hidden border border-[#f3c6c2]">
            <div className="px-5 py-2.5 bg-[#fff6f5] text-xs font-medium text-[#b3261e] flex items-center gap-2">
              <WarningCircle size={15} weight="fill" />
              {flagged.counts.rejected > 0 && <span>{flagged.counts.rejected} rejected</span>}
              {flagged.counts.rejected > 0 && (flagged.counts.needs_date_fix > 0 || flagged.counts.needs_review > 0) && <span>·</span>}
              {flagged.counts.needs_date_fix > 0 && <span>{flagged.counts.needs_date_fix} needing a date fix</span>}
              {flagged.counts.needs_date_fix > 0 && flagged.counts.needs_review > 0 && <span>·</span>}
              {flagged.counts.needs_review > 0 && <span>{flagged.counts.needs_review} to check</span>}
            </div>
            <div className="divide-y divide-riq-border">
              {flagged.items.map((r) => (
                <button key={r.id} data-testid={`home-flagged-${r.id}`} onClick={() => navigate(`/receipts/${r.id}`)} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-secondary/50 transition-colors text-left">
                  <MerchantLogo name={r.merchant_name} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate flex items-center gap-2">
                      <span className="truncate">{r.merchant_name}</span>
                      {r.review_status === "rejected"
                        ? <span className="rounded-full bg-[#FFE3E1] text-[#b3261e] px-2 py-0.5 text-[10px] font-bold uppercase shrink-0">Rejected</span>
                        : r.review_code === "duplicate"
                        ? <span className="rounded-full bg-[#FFF0D6] text-[#8a5a00] px-2 py-0.5 text-[10px] font-bold uppercase shrink-0">Duplicate</span>
                        : r.review_status === "needs_date_fix"
                        ? <span className="rounded-full bg-riq-gold/25 text-[#8a5a00] px-2 py-0.5 text-[10px] font-bold uppercase shrink-0">Fix date</span>
                        : <span className="rounded-full bg-riq-sage text-riq-primary px-2 py-0.5 text-[10px] font-bold uppercase shrink-0">Check total</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{r.review_reason || `${shortDate(r.date)} · ${money(r.total, cur)}`}</div>
                  </div>
                  <CaretRight size={16} className="text-neutral-300 shrink-0" />
                </button>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* Budget + Insights (secondary) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          <div className="flex items-center gap-2 mb-4"><Wallet size={20} weight="duotone" className="text-riq-primary" /><h3 className="font-head font-semibold text-riq-primary">Monthly Budget</h3></div>
          {budget.monthly_budget ? (
            <div>
              <div className="flex items-end justify-between">
                <div><div className="text-xs text-muted-foreground">Spent</div><div data-testid="budget-spent" className="font-head text-2xl font-bold text-riq-primary">{money(budgetSpent, cur)}</div></div>
                <div className="text-right"><div className="text-xs text-muted-foreground">Remaining</div><div className="font-head text-2xl font-bold" style={{ color: budgetRemaining >= 0 ? "#6E3FFC" : "#FF6B6B" }}>{money(budgetRemaining, cur)}</div></div>
              </div>
              <div className="mt-4 h-3 rounded-full bg-secondary overflow-hidden">
                <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(budgetPctUsed || 0, 100)}%` }} transition={{ duration: 0.8, ease: "easeOut" }}
                  className="h-full rounded-full" style={{ background: budgetPctUsed > 90 ? "#FF6B6B" : "#6E3FFC" }} />
              </div>
              <div className="flex justify-between mt-2 text-xs text-muted-foreground"><span>{budgetPctUsed}% used</span><span>of {money(budget.monthly_budget, cur)}</span></div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">No budget set. <button onClick={() => navigate("/settings")} className="text-riq-primary font-medium">Set one →</button></div>
          )}
        </Card>

        {insights?.length > 0 ? (
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2"><Sparkle size={18} weight="duotone" className="text-riq-gold" /> Insights</h3>
              <button data-testid="home-see-insights" onClick={() => navigate("/insights")} className="text-sm text-riq-primary flex items-center gap-1">See all <ArrowRight size={14} /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {insights.slice(0, 2).map((ins, i) => (
                <button key={i} data-testid={`home-insight-${i}`} onClick={() => navigate(ins.item ? `/items/${encodeURIComponent(ins.item)}` : "/insights")} className="w-full text-left">
                  <Card className="flex items-start gap-3 p-4 h-full hover:-translate-y-0.5 transition-transform">
                    <div className="w-9 h-9 rounded-xl bg-riq-gold/20 flex items-center justify-center shrink-0"><Sparkle size={18} weight="duotone" className="text-riq-primary" /></div>
                    <div className="min-w-0">
                      {ins.title && <div className="font-head font-semibold text-riq-primary text-sm">{ins.title}</div>}
                      <p className="text-sm leading-relaxed text-neutral-600 mt-0.5">{ins.text}</p>
                    </div>
                  </Card>
                </button>
              ))}
            </div>
          </div>
        ) : <div className="lg:col-span-2" />}
      </div>

      {/* Subscriptions (before Warranties) with drill-through */}
      {subs && subs.summary && subs.summary.count > 0 && (
        <SubscriptionsSection data={subs} cur={cur} onManage={() => navigate("/subscriptions")} onAdd={() => setAddSub({})} />
      )}

      {/* Warranties */}
      {warranties?.counts?.total > 0 && (
        <div>
          <SectionTitle action={<button data-testid="home-see-warranties" onClick={() => navigate("/warranties")} className="text-sm text-riq-primary flex items-center gap-1">All warranties <ArrowRight size={14} /></button>}>Warranties</SectionTitle>
          <Card data-testid="home-warranty-summary" className="p-0 overflow-hidden">
            <div className="grid grid-cols-3 divide-x divide-riq-border border-b border-riq-border">
              <WarrStat label="Active" value={warranties.counts.active} color="#22B573" />
              <WarrStat label="Expiring soon" value={warranties.counts.expiring_soon} color="#B25E00" testId="home-warranty-expiring" />
              <WarrStat label="Expired" value={warranties.counts.expired} color="#FF6B6B" />
            </div>
            {warranties.upcoming.length > 0 ? (
              <div className="divide-y divide-riq-border">
                {warranties.upcoming.map((w) => (
                  <button key={w.id} data-testid={`home-warranty-${w.id}`} onClick={() => navigate("/warranties")} className="w-full flex items-center gap-4 px-5 py-4 hover:bg-secondary/50 transition-colors text-left">
                    <MerchantLogo name={w.merchant_name} size={40} />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{w.label}</div>
                      <div className="text-xs text-muted-foreground">{periodLabel(w.period_months)} cover · expires {shortDate(w.end_date)}</div>
                    </div>
                    <WarrPill status={w.status} days={w.days_until} />
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-5 py-4 text-sm text-muted-foreground">All tracked warranties have expired. <button onClick={() => navigate("/warranties")} className="text-riq-primary font-medium">Review →</button></div>
            )}
          </Card>
        </div>
      )}

      {/* How receipts were added — capture method tally (bottom of dashboard) */}
      <SourceCounter counts={summary.source_counts} />

      {/* Add subscription dialog (invoked from donut + subs section) */}
      <SubscriptionDialog editing={addSub} cur={cur} onClose={() => setAddSub(null)} onSaved={() => { setAddSub(null); loadSubs(); }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Monthly Expense Trend — weekly (this month) / 6-month toggle        */
/* ------------------------------------------------------------------ */

// Step a date forward/back by a subscription's cadence.
function cadenceAdd(d, cadence, dir) {
  const nd = new Date(d);
  if (cadence === "weekly") nd.setDate(nd.getDate() + 7 * dir);
  else if (cadence === "quarterly") nd.setMonth(nd.getMonth() + 3 * dir);
  else if (cadence === "yearly") nd.setFullYear(nd.getFullYear() + dir);
  else nd.setMonth(nd.getMonth() + dir); // monthly
  return nd;
}

// Full billing amounts of a subscription that fall within [start, end] (inclusive).
function subOccurrences(sub, start, end) {
  const anchorStr = sub.next_billing_date || sub.next_date;
  const amount = Number(sub.amount) || 0;
  if (!anchorStr || amount <= 0) return [];
  const cadence = sub.cadence || "monthly";
  let d = new Date(anchorStr);
  if (isNaN(d)) return [];
  let guard = 0;
  while (d > start && guard < 800) { d = cadenceAdd(d, cadence, -1); guard++; }
  const out = [];
  guard = 0;
  while (d <= end && guard < 800) {
    if (d >= start && d <= end) out.push(amount);
    d = cadenceAdd(d, cadence, 1);
    guard++;
  }
  return out;
}

function MonthlyTrend({ weekly, monthly, subsList = [], cur }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState("weekly");
  const pad = (n) => String(n).padStart(2, "0");

  const weeklyData = useMemo(() => {
    if (!weekly?.length) return [];
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth() + 1;
    const daysInMonth = new Date(y, m, 0).getDate();
    const numWeeks = Math.ceil(daysInMonth / 7);
    const monthShort = now.toLocaleString("en", { month: "short" });
    // Full subscription amounts land on the week that contains their billing day.
    const monthStart = new Date(y, m - 1, 1);
    const monthEnd = new Date(y, m - 1, daysInMonth);
    const weekSubs = {};
    subsList.forEach((sub) => {
      const anchorStr = sub.next_billing_date || sub.next_date;
      const amt = Number(sub.amount) || 0;
      if (!anchorStr || amt <= 0) return;
      const cadence = sub.cadence || "monthly";
      let d = new Date(anchorStr);
      if (isNaN(d)) return;
      let guard = 0;
      while (d > monthEnd && guard < 800) { d = cadenceAdd(d, cadence, -1); guard++; }
      guard = 0;
      while (d <= monthEnd && guard < 800) {
        if (d >= monthStart) {
          const wk = Math.min(Math.ceil(d.getDate() / 7), numWeeks);
          weekSubs[wk] = (weekSubs[wk] || 0) + amt;
        }
        d = cadenceAdd(d, cadence, 1);
        guard++;
      }
    });
    const buckets = {};
    const catBuckets = {};
    weekly.forEach((d) => {
      const day = new Date(d.label).getDate();
      const wk = Math.min(Math.ceil(day / 7), numWeeks);
      buckets[wk] = (buckets[wk] || 0) + d.value;
      const cb = catBuckets[wk] || (catBuckets[wk] = {});
      Object.entries(d.cats || {}).forEach(([c, v]) => { cb[c] = (cb[c] || 0) + v; });
    });
    return Array.from({ length: numWeeks }, (_, i) => {
      const wk = i + 1;
      const startDay = i * 7 + 1;
      const endDay = Math.min((i + 1) * 7, daysInMonth);
      const cats = { ...(catBuckets[wk] || {}) };
      if (weekSubs[wk]) cats["Subscriptions"] = Math.round(((cats["Subscriptions"] || 0) + weekSubs[wk]) * 100) / 100;
      const value = Math.round(Object.values(cats).reduce((s, v) => s + v, 0) * 100) / 100;
      return {
        label: `Week ${i + 1}`,
        value,
        from: `${y}-${pad(m)}-${pad(startDay)}`,
        to: `${y}-${pad(m)}-${pad(endDay)}`,
        rangeLabel: `Week ${i + 1} · ${monthShort} ${startDay}–${endDay}`,
        cats, ...cats,
      };
    });
  }, [weekly, subsList]);

  const monthlyData = useMemo(() => (monthly || []).map((mo) => {
    const [y, mm] = mo.label.split("-");
    const yi = Number(y);
    const mi = Number(mm);
    const name = new Date(yi, mi - 1, 1).toLocaleString("en", { month: "short" });
    const lastDay = new Date(yi, mi, 0).getDate();
    const ms = new Date(yi, mi - 1, 1);
    const me = new Date(yi, mi - 1, lastDay);
    let subsInMonth = 0;
    subsList.forEach((sub) => { subOccurrences(sub, ms, me).forEach((a) => { subsInMonth += a; }); });
    const cats = { ...(mo.cats || {}) };
    if (subsInMonth) cats["Subscriptions"] = Math.round(((cats["Subscriptions"] || 0) + subsInMonth) * 100) / 100;
    const value = Math.round(Object.values(cats).reduce((s, v) => s + v, 0) * 100) / 100;
    return {
      label: name,
      value,
      from: `${mo.label}-01`,
      to: `${mo.label}-${pad(lastDay)}`,
      rangeLabel: new Date(yi, mi - 1, 1).toLocaleString("en", { month: "long", year: "numeric" }),
      cats, ...cats,
    };
  }), [monthly, subsList]);

  // Ordered category list + colours for the stacked bars.
  const catOrder = useMemo(() => {
    const totals = {};
    (mode === "weekly" ? weeklyData : monthlyData).forEach((d) => {
      Object.entries(d.cats || {}).forEach(([c, v]) => { totals[c] = (totals[c] || 0) + v; });
    });
    return Object.keys(totals).sort((a, b) => {
      const tail = (c) => (c === "Subscriptions" ? 2 : c === "Other" ? 1 : 0);
      if (tail(a) !== tail(b)) return tail(a) - tail(b);
      return totals[b] - totals[a];
    });
  }, [weeklyData, monthlyData, mode]);

  const CAT_PALETTE = ["#6E3FFC", "#2BB0C4", "#42C97C", "#B36EFF", "#FF6B6B", "#F59FB5", "#5B8DEF", "#F2994A"];
  const catColor = (c, i) => (c === "Subscriptions" ? "#F5A623" : c === "Other" ? "#8a8fa3" : CAT_PALETTE[i % CAT_PALETTE.length]);

  const chartData = mode === "weekly" ? weeklyData : monthlyData;
  const hasData = chartData.some((d) => d.value > 0);

  const openRange = (d) => {
    if (!d || !(d.value > 0)) return;
    navigate(`/receipts?from=${d.from}&to=${d.to}&label=${encodeURIComponent(d.rangeLabel)}`);
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-head font-semibold text-riq-primary flex items-center gap-2"><ChartBar size={18} weight="duotone" /> Monthly Expense Trend</h3>
        <div className="flex items-center gap-1 rounded-full bg-secondary p-1">
          {[{ k: "weekly", l: "This month" }, { k: "monthly", l: "6 months" }].map((t) => (
            <button
              key={t.k}
              data-testid={`trend-mode-${t.k}`}
              onClick={() => setMode(t.k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${mode === t.k ? "bg-riq-primary text-white" : "text-neutral-500"}`}
            >
              {t.l}
            </button>
          ))}
        </div>
      </div>
      {hasData && (
        <>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2" data-testid="trend-legend">
            {catOrder.map((c, i) => (
              <span key={c} className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                <span className="h-2.5 w-2.5 rounded-sm" style={{ background: catColor(c, i) }} /> {c}
              </span>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mb-3">Tap a bar to see those receipts.</p>
        </>
      )}
      {hasData ? (
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} margin={{ left: -14, right: 4, top: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef0f4" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#8a8fa3" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: "#b0b4c3" }} axisLine={false} tickLine={false} width={48} tickFormatter={(v) => money(v, cur).replace(/\.\d+$/, "")} />
            <Tooltip content={<StackTooltip cur={cur} />} cursor={{ fill: "#6E3FFC0d" }} />
            {catOrder.map((c, i) => (
              <Bar key={c} dataKey={c} stackId="spend" fill={catColor(c, i)} maxBarSize={56} cursor="pointer" onClick={openRange}
                radius={i === catOrder.length - 1 ? [8, 8, 0, 0] : [0, 0, 0, 0]} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div className="py-10 text-center text-sm text-muted-foreground">No spending recorded {mode === "weekly" ? "this month" : "in the last 6 months"} yet.</div>
      )}
    </Card>
  );
}

function StackTooltip({ active, payload, label, cur }) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value > 0).sort((a, b) => b.value - a.value);
  if (!rows.length) return null;
  const total = rows.reduce((s, p) => s + p.value, 0);
  return (
    <div style={{ borderRadius: 12, border: "1px solid #E9EAF2", background: "#fff", padding: "10px 12px", fontSize: 12, boxShadow: "0 6px 24px rgba(20,10,40,0.08)" }}>
      <div style={{ fontWeight: 700, marginBottom: 6, color: "#1A1030" }}>{label} · {money(total, cur)}</div>
      {rows.map((p) => (
        <div key={p.dataKey} style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: p.color, display: "inline-block" }} />
          <span style={{ flex: 1, color: "#4a5568" }}>{p.dataKey}</span>
          <span style={{ fontWeight: 600, color: "#1A1030" }}>{money(p.value, cur)}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Expense Categories — multicolour donut + legend                     */
/* ------------------------------------------------------------------ */

function CategoryDonut({ categories, total, subsTotal = 0, cur, onSelect, onAddSubscription }) {
  const navigate = useNavigate();
  const data = (categories || []).filter((c) => c.total > 0);
  if (!data.length) return null;
  const grand = total || data.reduce((s, c) => s + c.total, 0) || 1;
  // Brand-led, colourful ring — largest slice takes the Sliply violet.
  const PALETTE = ["#6E3FFC", "#2BB0C4", "#F5A623", "#42C97C", "#B36EFF", "#FF6B6B", "#F59FB5", "#8a8fa3"];
  const hue = (i) => PALETTE[i % PALETTE.length];

  return (
    <div>
      <SectionTitle
        action={
          <div className="flex items-center gap-3">
            {onAddSubscription && (
              <button data-testid="donut-add-subscription" onClick={onAddSubscription} className="text-sm text-riq-primary flex items-center gap-1 font-medium">
                <Plus size={14} weight="bold" /> Subscription
              </button>
            )}
            <button data-testid="home-analytics-link" onClick={() => navigate("/analytics")} className="text-sm text-riq-primary flex items-center gap-1">Analytics <ArrowRight size={14} /></button>
          </div>
        }
      >
        Expenses &amp; subscriptions
      </SectionTitle>
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 items-center">
          {/* Donut */}
          <div className="relative mx-auto" style={{ width: 200, height: 200 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="total" nameKey="category" innerRadius={62} outerRadius={92} paddingAngle={2} stroke="none">
                  {data.map((c, i) => <Cell key={c.category} fill={hue(i)} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [money(v, cur), n]} contentStyle={{ borderRadius: 12, border: "1px solid #E9EAF2", fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total</div>
              <div className="font-head text-lg font-bold text-riq-primary">{money(grand, cur)}</div>
              {subsTotal > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">incl. {money(subsTotal, cur)} subs</div>}
            </div>
          </div>
          {/* Legend */}
          <div className="space-y-1">
            {data.map((c, i) => {
              const pct = Math.round((c.total / grand) * 100);
              return (
                <button
                  key={c.category}
                  data-testid={`category-row-${c.category}`}
                  onClick={() => onSelect(c.category)}
                  className="w-full flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-secondary/60 transition-colors text-left"
                >
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: hue(i) }} />
                  <span className="flex-1 min-w-0 truncate text-sm font-medium text-neutral-700">{c.category}</span>
                  <span className="text-xs font-semibold text-muted-foreground tabular-nums">{pct}%</span>
                  <span className="font-head font-semibold text-sm text-riq-primary tabular-nums w-20 text-right">{money(c.total, cur)}</span>
                </button>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Subscriptions section (drill-through) — shown before Warranties     */
/* ------------------------------------------------------------------ */

const SUB_CADENCE_LABEL = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

function SubscriptionsSection({ data, cur, onManage, onAdd }) {
  const [openId, setOpenId] = useState(null);
  const active = (data.subscriptions || []).filter((s) => s.active);
  const s = data.summary || {};

  return (
    <div>
      <SectionTitle
        action={
          <div className="flex items-center gap-3">
            <button data-testid="subs-section-add" onClick={onAdd} className="text-sm text-riq-primary flex items-center gap-1 font-medium"><Plus size={14} weight="bold" /> Add</button>
            <button data-testid="home-see-subscriptions" onClick={onManage} className="text-sm text-riq-primary flex items-center gap-1">Manage <ArrowRight size={14} /></button>
          </div>
        }
      >
        Subscriptions
      </SectionTitle>
      <Card data-testid="home-subscriptions-section" className="!p-0 overflow-hidden">
        {/* Summary strip */}
        <div className="grid grid-cols-3 divide-x divide-riq-border border-b border-riq-border">
          <div className="px-4 py-3 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Per month</div>
            <div className="font-head text-xl font-bold text-riq-primary mt-0.5">{money(s.monthly_total || 0, cur)}</div>
          </div>
          <div className="px-4 py-3 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Active</div>
            <div className="font-head text-xl font-bold text-neutral-700 mt-0.5">{s.count || 0}</div>
          </div>
          <div className="px-4 py-3 text-center">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Due soon</div>
            <div className="font-head text-xl font-bold mt-0.5" style={{ color: s.due_soon ? "#B25E00" : "#22B573" }}>{s.due_soon || 0}</div>
          </div>
        </div>
        {/* Drill-through list */}
        <div className="divide-y divide-riq-border">
          {active.map((sub) => {
            const isOpen = openId === sub.id;
            return (
              <div key={sub.id}>
                <button
                  data-testid={`home-sub-row-${sub.id}`}
                  onClick={() => setOpenId(isOpen ? null : sub.id)}
                  className="w-full flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left"
                >
                  <div className="grid h-10 w-10 place-items-center rounded-xl shrink-0 bg-riq-primary/10 text-riq-primary"><ArrowsClockwise size={18} weight="duotone" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{sub.name}</div>
                    <div className="text-xs text-muted-foreground truncate">{SUB_CADENCE_LABEL[sub.cadence] || sub.cadence} · {sub.category}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-head font-semibold text-riq-primary">{money(sub.amount, cur)}</div>
                    <div className="text-[11px] text-muted-foreground">≈ {money(sub.monthly_cost, cur)}/mo</div>
                  </div>
                  <CaretRight size={16} className={`text-neutral-300 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                </button>
                {isOpen && (
                  <div data-testid={`home-sub-detail-${sub.id}`} className="px-4 sm:px-5 pb-4 -mt-1">
                    <div className="rounded-xl bg-secondary/60 p-3 grid grid-cols-2 gap-y-2 gap-x-4 text-sm">
                      <Detail label="Billing cycle" value={SUB_CADENCE_LABEL[sub.cadence] || sub.cadence} />
                      <Detail label="Category" value={sub.category} />
                      <Detail label="Next charge" value={shortDate(sub.next_date)} />
                      <Detail label="Monthly equiv." value={`${money(sub.monthly_cost, cur)}/mo`} />
                      {sub.payment_method && <Detail label="Payment" value={sub.payment_method} />}
                      <Detail label="Status" value={sub.status === "due_soon" ? `Due in ${Math.max(sub.days_until, 0)}d` : `${sub.days_until}d away`} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-neutral-700 font-medium truncate">{value}</div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Small helpers                                                       */
/* ------------------------------------------------------------------ */

function HeroStat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-2xl bg-white/10 px-4 py-3 backdrop-blur-sm">
      <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/60"><Icon size={13} weight="bold" /> {label}</div>
      <div data-testid={testId} className="font-head text-xl font-bold mt-0.5">{value}</div>
    </div>
  );
}

const SOURCE_ORDER = ["scan", "upload", "pdf", "email"];

function SourceCounter({ counts }) {
  const entries = SOURCE_ORDER
    .filter((k) => counts && counts[k] > 0)
    .map((k) => ({ key: k, count: counts[k], ...sourceMeta(k) }));
  if (entries.length === 0) return null;
  return (
    <Card data-testid="home-source-counter" className="!p-0 overflow-hidden">
      <div className="px-5 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        How you added receipts · {monthLabel()}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 p-4">
        {entries.map(({ key, count, label, Icon, color }) => (
          <div key={key} data-testid={`source-count-${key}`} className="rounded-2xl bg-secondary/50 px-3 py-3 text-center">
            <div className="mx-auto mb-1.5 grid h-8 w-8 place-items-center rounded-xl" style={{ background: `${color}1a`, color }}>
              <Icon size={16} weight="bold" />
            </div>
            <div className="font-head text-2xl font-bold text-riq-primary leading-none">{count}</div>
            <div className="mt-1 text-[11px] font-medium text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}


function WarrStat({ label, value, color, testId }) {
  return (
    <div className="px-4 py-4 text-center">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div data-testid={testId} className="font-head text-2xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function WarrPill({ status, days }) {
  if (status === "expired") return <span className="rounded-full bg-[#FFECEC] text-[#FF6B6B] px-2.5 py-1 text-xs font-semibold flex items-center gap-1 shrink-0"><WarningCircle size={12} weight="fill" /> Expired</span>;
  if (status === "expiring_soon") return <span className="rounded-full bg-riq-gold/25 text-[#B25E00] px-2.5 py-1 text-xs font-semibold flex items-center gap-1 shrink-0"><Clock size={12} weight="fill" /> {Math.max(days, 0)}d left</span>;
  return <span className="rounded-full bg-[#E4F7ED] text-[#22B573] px-2.5 py-1 text-xs font-semibold flex items-center gap-1 shrink-0"><CheckCircle size={12} weight="fill" /> {days}d left</span>;
}
